import React, { useState } from 'react';
import { FileText, Users, Calendar, Info } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ContractFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function ContractForm({ onSuccess, onCancel }: ContractFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    terms: '',
    validUntil: '',
    participants: [] as { email: string; role: 'signer' | 'viewer' }[]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [participantEmail, setParticipantEmail] = useState('');
  const [participantRole, setParticipantRole] = useState<'signer' | 'viewer'>('signer');

  const addParticipant = () => {
    if (!participantEmail.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      participants: [
        ...prev.participants,
        { email: participantEmail.trim(), role: participantRole }
      ]
    }));
    setParticipantEmail('');
    setParticipantRole('signer');
  };

  const removeParticipant = (email: string) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.filter(p => p.email !== email)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create contract
      const { data: contract, error: contractError } = await supabase
        .from('contracts')
        .insert({
          title: formData.title,
          description: formData.description,
          terms: formData.terms,
          valid_until: formData.validUntil,
          created_by: user.id,
          status: 'draft'
        })
        .select()
        .single();

      if (contractError) throw contractError;

      // Add participants
      const participantPromises = formData.participants.map(async participant => {
        // Get user ID from email
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', participant.email)
          .single();

        if (!profiles?.id) {
          throw new Error(`User not found: ${participant.email}`);
        }

        return supabase
          .from('contract_participants')
          .insert({
            contract_id: contract.id,
            user_id: profiles.id,
            role: participant.role,
            status: 'pending'
          });
      });

      await Promise.all(participantPromises);

      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contract');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Contract Title
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 border rounded-md"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border rounded-md"
          rows={3}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Contract Terms
        </label>
        <textarea
          value={formData.terms}
          onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
          className="w-full px-3 py-2 border rounded-md font-mono text-sm"
          rows={10}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valid Until
        </label>
        <input
          type="date"
          value={formData.validUntil}
          onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
          className="w-full px-3 py-2 border rounded-md"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Add Participants
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="email"
            value={participantEmail}
            onChange={(e) => setParticipantEmail(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-md"
            placeholder="Email address"
          />
          <select
            value={participantRole}
            onChange={(e) => setParticipantRole(e.target.value as 'signer' | 'viewer')}
            className="px-3 py-2 border rounded-md"
          >
            <option value="signer">Signer</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            type="button"
            onClick={addParticipant}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Add
          </button>
        </div>

        {formData.participants.length > 0 && (
          <div className="mt-4 space-y-2">
            {formData.participants.map((participant, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span>{participant.email}</span>
                  <span className="text-sm text-gray-500">({participant.role})</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeParticipant(participant.email)}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-blue-50 p-4 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-500 mt-0.5" />
        <div className="text-sm text-blue-700">
          <p className="font-medium mb-1">Important Notes:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>All signers must approve for the contract to become active</li>
            <li>Viewers can see the contract but cannot sign</li>
            <li>You cannot modify the contract after it becomes active</li>
            <li>Make sure all terms are clearly stated to avoid confusion</li>
          </ul>
        </div>
      </div>

      <div className="flex justify-end gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <>
              <span className="animate-spin">⌛</span>
              Creating...
            </>
          ) : (
            <>
              <FileText className="w-5 h-5" />
              Create Contract
            </>
          )}
        </button>
      </div>
    </form>
  );
}